<h1 mat-dialog-title>
  <span class="icon-text-container">
    <mat-icon class="margin-icon">warning</mat-icon>
    {{ 'affectedPackaging.dialog.title' | transloco }}
  </span>
</h1>

<mat-dialog-content>
  <p *ngIf="affectedCount === 0">{{ 'affectedPackaging.dialog.messageOnlyEdited' | transloco }}</p>
  <p *ngIf="affectedCount > 0">
    {{ (affectedCount === 1 ? 'affectedPackaging.dialog.messageSingle' : 'affectedPackaging.dialog.messageMultiple')
      | transloco: { count: affectedCount } }}
  </p>

  <form *ngIf="showDocumentSection" [formGroup]="form" class="documents-section">
    <h3>{{ 'affectedPackaging.documents.title' | transloco }}</h3>
    <p>{{ 'affectedPackaging.documents.description' | transloco }}</p>

    <p *ngIf="!allowRecreate" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon">delete_outline</mat-icon>
      <span>{{ 'affectedPackaging.documents.deleteOnlyNotice' | transloco }}</span>
    </p>

    <mat-radio-group *ngIf="allowRecreate" formControlName="mode" class="mode-group">
      <mat-radio-button [value]="modes.Delete">
        <span class="mode-label">{{ 'affectedPackaging.documents.deleteLabel' | transloco }}</span>
        <span class="hint mode-description">{{ 'affectedPackaging.documents.deleteDescription' | transloco }}</span>
      </mat-radio-button>
      <mat-radio-button [value]="modes.Recreate" [disabled]="!canRecreate">
        <span class="mode-label">{{ 'affectedPackaging.documents.recreateLabel' | transloco }}</span>
        <span class="hint mode-description">{{ 'affectedPackaging.documents.recreateDescription' | transloco }}</span>
      </mat-radio-button>
    </mat-radio-group>

    <div *ngIf="isRecreate && recreateOptions" class="options-summary">
      <span class="icon-text-container">
        <mat-icon class="margin-icon" color="primary">tune</mat-icon>
        <span>{{ recreateOptions.summary }}</span>
      </span>
      <button mat-stroked-button type="button" (click)="openOptionsDialog()">
        {{ 'affectedPackaging.documents.optionsChange' | transloco }}
      </button>
    </div>

    <p *ngIf="organizationLoadFailed" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">error</mat-icon>
      <span>{{ 'affectedPackaging.documents.organizationLoadFailed' | transloco }}</span>
    </p>

    <p *ngIf="allowRecreate && !allWritable" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">lock</mat-icon>
      <span>{{ 'affectedPackaging.documents.recreateUnavailable' | transloco: { packaging: nonWritableNames } }}</span>
    </p>

    <p *ngIf="allowRecreate && hasForeignOrganization" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">domain_disabled</mat-icon>
      <span>
        {{ 'affectedPackaging.documents.recreateForeignOrganization'
          | transloco: { organizations: foreignOrganizationNames } }}
        <ng-container *ngIf="hasDeclarationOfConformity">
          {{ 'affectedPackaging.documents.recreateForeignOrganizationDoc' | transloco }}
        </ng-container>
      </span>
    </p>

  </form>

  <p *ngIf="hasDocuments && !canRecreate" class="icon-text-container mode-warning documents-section">
    <mat-icon class="margin-icon">delete_outline</mat-icon>
    <span>{{ 'affectedPackaging.documents.deleteForcedNotice' | transloco }}</span>
  </p>

  <div class="table-container-wide">
    <table mat-table [dataSource]="dataSource" matSort>
      <ng-container matColumnDef="packagingType">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.packagingType' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ getPackagingTypeKey(element.packagingType) | transloco }}</td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.name' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.name }}
          <span *ngIf="element.isEditedPackaging" class="edited-marker">
            {{ 'affectedPackaging.table.editedPackaging' | transloco }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="articleNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.articleNumber' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.articleNumber }}</td>
      </ng-container>

      <ng-container matColumnDef="brandName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.brandName' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.brandName }}</td>
      </ng-container>

      <ng-container matColumnDef="version">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.version' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.version }}<span *ngIf="element.versionName"> &ndash; {{ element.versionName }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="organizationName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.organizationName' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.organizationName }}</td>
      </ng-container>

      <ng-container matColumnDef="recreateAllowed">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'affectedPackaging.table.recreateAllowedTooltip' | transloco">
          {{ 'affectedPackaging.table.recreateAllowed' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="isRecreateAllowedFor(element)">check</mat-icon>
          <span *ngIf="!isRecreateAllowedFor(element)">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="technicalDocumentation">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'ppwrDocuments.typeTd' | transloco">
          {{ 'affectedPackaging.table.technicalDocumentation' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="element.hasTechnicalDocumentation">check</mat-icon>
          <span *ngIf="!element.hasTechnicalDocumentation">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="declarationOfConformity">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'ppwrDocuments.typeDoc' | transloco">
          {{ 'affectedPackaging.table.declarationOfConformity' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="element.hasDeclarationOfConformity">check</mat-icon>
          <span *ngIf="!element.hasDeclarationOfConformity">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="inaccessibleLabel">
        <td mat-cell *matCellDef="let element" [attr.colspan]="inaccessibleLabelSpan" class="inaccessible-label">
          <span class="icon-text-container">
            <mat-icon class="margin-icon">visibility_off</mat-icon>
            {{ 'affectedPackaging.table.inaccessible' | transloco: { count: element.count } }}
          </span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns; when: isAccessibleRow"></tr>
      <tr mat-row *matRowDef="let row; columns: inaccessibleColumns; when: isInaccessibleRow"></tr>
    </table>
  </div>

</mat-dialog-content>

<mat-dialog-actions>
  <button (click)="doConfirm()" mat-raised-button type="button" color="primary">
    {{ 'common.buttons.save' | transloco }}
  </button>
  <button (click)="doReject()" mat-raised-button type="button">
    {{ 'common.buttons.cancel' | transloco }}
  </button>
</mat-dialog-actions>
